import { useCallback, useMemo, useRef, useState } from 'react';
import type { CanvasObject } from '../types';

export function useObjects(defaultColor: string = '#007bff') {
  const [objects, setObjects] = useState<Map<string, CanvasObject>>(new Map());
  const [zOrder, setZOrder] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const tempRectRef = useRef<(CanvasObject & { startX: number; startY: number }) | null>(null);

  const getObjectsArray = useCallback(() => {
    return zOrder.map(id => objects.get(id)!).filter(Boolean);
  }, [objects, zOrder]);

  const getObjectById = useCallback((id: string | null) => {
    if (!id) return null;
    return objects.get(id) || null;
  }, [objects]);

  const beginRect = useCallback((x: number, y: number) => {
    tempRectRef.current = {
      id: '__temp__rect',
      type: 'rectangle',
      x,
      y,
      width: 0,
      height: 0,
      rotation: 0,
      backgroundColor: defaultColor,
      startX: x,
      startY: y
    };
  }, [defaultColor]);

  const beginCircle = useCallback((x: number, y: number) => {
    tempRectRef.current = {
      id: '__temp__circle',
      type: 'circle',
      x,
      y,
      width: 0,
      height: 0,
      rotation: 0,
      backgroundColor: defaultColor,
      startX: x,
      startY: y
    };
  }, [defaultColor]);

  const updateRect = useCallback((x: number, y: number, shiftKey?: boolean) => {
    const r = tempRectRef.current;
    if (!r) return;
    let nx = Math.round(Math.min(r.startX, x));
    let ny = Math.round(Math.min(r.startY, y));
    let w = Math.round(Math.abs(x - r.startX));
    let h = Math.round(Math.abs(y - r.startY));
    
    // Shift 키가 눌려있으면 정사각형으로 만들기
    if (shiftKey && w > 0 && h > 0) {
      const size = Math.max(w, h);
      w = size;
      h = size;
      // 시작점 기준으로 위치 조정
      if (x < r.startX) {
        nx = Math.round(r.startX - size);
      } else {
        nx = Math.round(r.startX);
      }
      if (y < r.startY) {
        ny = Math.round(r.startY - size);
      } else {
        ny = Math.round(r.startY);
      }
    }
    
    tempRectRef.current = { ...r, x: nx, y: ny, width: w, height: h };
  }, []);

  const updateCircle = useCallback((x: number, y: number, shiftKey?: boolean) => {
    const r = tempRectRef.current;
    if (!r) return;
    let nx = Math.round(Math.min(r.startX, x));
    let ny = Math.round(Math.min(r.startY, y));
    let w = Math.round(Math.abs(x - r.startX));
    let h = Math.round(Math.abs(y - r.startY));
    
    // Shift 키가 눌려있으면 정원으로 만들기
    if (shiftKey && w > 0 && h > 0) {
      const size = Math.max(w, h);
      w = size;
      h = size;
      // 시작점 기준으로 위치 조정
      if (x < r.startX) {
        nx = Math.round(r.startX - size);
      } else {
        nx = Math.round(r.startX);
      }
      if (y < r.startY) {
        ny = Math.round(r.startY - size);
      } else {
        ny = Math.round(r.startY);
      }
    }
    
    tempRectRef.current = { ...r, x: nx, y: ny, width: w, height: h };
  }, []);

  const commitRect = useCallback(() => {
    const r = tempRectRef.current;
    if (!r || r.width === 0 || r.height === 0) {
      tempRectRef.current = null;
      return;
    }
    const id = `${r.type}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    // 최종 정수 스냅 확정
    const rect: CanvasObject = {
      ...r,
      id,
      x: Math.round(r.x),
      y: Math.round(r.y),
      width: Math.round(r.width),
      height: Math.round(r.height)
    };
    setObjects(prev => {
      const next = new Map(prev);
      next.set(id, rect);
      return next;
    });
    setZOrder(prev => [...prev, id]);
    tempRectRef.current = null;
  }, []);

  const commitCircle = commitRect;

  const clearTemp = useCallback(() => {
    tempRectRef.current = null;
  }, []);

  // 개별 객체 업데이트
  const updateObject = useCallback((id: string, updater: (obj: CanvasObject) => CanvasObject) => {
    setObjects(prev => {
      const next = new Map(prev);
      const cur = next.get(id);
      if (!cur) return prev;
      const updated = updater(cur);
      next.set(id, updated);
      return next;
    });
  }, []);

  // 다중 선택 관리 함수들
  const addToSelection = useCallback((id: string) => {
    setSelectedIds(prev => new Set([...Array.from(prev), id]));
    setSelectedId(id); // 마지막 선택된 객체를 primary로 설정
  }, []);

  const removeFromSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    // primary 선택도 해제
    if (selectedId === id) {
      const remaining = Array.from(selectedIds).filter(sid => sid !== id);
      setSelectedId(remaining.length > 0 ? remaining[remaining.length - 1] : null);
    }
  }, [selectedId, selectedIds]);

  const setSelection = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
    setSelectedId(ids.length > 0 ? ids[ids.length - 1] : null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectedId(null);
  }, []);

  // 선택된 객체 배열 반환 (다중 선택 우선)
  const getSelectedObjects = useCallback(() => {
    if (selectedIds.size > 0) {
      return Array.from(selectedIds).map(id => objects.get(id)).filter(Boolean) as CanvasObject[];
    }
    if (selectedId) {
      const obj = objects.get(selectedId);
      return obj ? [obj] : [];
    }
    return [];
  }, [selectedIds, selectedId, objects]);

  const moveSelectedBy = useCallback((dx: number, dy: number, targetIds?: string[]) => {
    const ids = targetIds || (selectedIds.size > 0 ? Array.from(selectedIds) : (selectedId ? [selectedId] : []));
    if (ids.length === 0) return;
    
    ids.forEach(id => {
      updateObject(id, (obj) => ({
        ...obj,
        x: Math.round(obj.x + dx),
        y: Math.round(obj.y + dy)
      }));
    });
  }, [selectedId, selectedIds, updateObject]);

  // 다중 선택 회전
  const rotateSelectedBy = useCallback((
    centerX: number,
    centerY: number,
    deltaAngle: number,
    initialStates: Map<string, { x: number; y: number; width: number; height: number; rotation: number }>
  ) => {
    const cos = Math.cos(deltaAngle);
    const sin = Math.sin(deltaAngle);
    
    initialStates.forEach((initialState, id) => {
      const obj = objects.get(id);
      if (!obj) return;
      
      // 초기 객체 중심
      const initialObjCenterX = initialState.x + initialState.width / 2;
      const initialObjCenterY = initialState.y + initialState.height / 2;
      
      // AABB 중심 기준 상대 위치
      const relX = initialObjCenterX - centerX;
      const relY = initialObjCenterY - centerY;
      
      // 회전 변환 적용
      const newRelX = relX * cos - relY * sin;
      const newRelY = relX * sin + relY * cos;
      
      // 새 객체 중심 위치
      const newObjCenterX = centerX + newRelX;
      const newObjCenterY = centerY + newRelY;
      
      // 새 객체 위치 (중심 기준)
      const newObjX = Math.round(newObjCenterX - initialState.width / 2);
      const newObjY = Math.round(newObjCenterY - initialState.height / 2);
      
      // 새 회전 각도 (초기 회전 + 델타)
      const newRotation = initialState.rotation + deltaAngle;
      
      updateObject(id, (o) => ({
        ...o,
        x: newObjX,
        y: newObjY,
        rotation: newRotation
      }));
    });
  }, [objects, updateObject]);

  const resizeSelected = useCallback((
    handle: string, 
    newX: number, newY: number, 
    startX: number, startY: number, 
    startWidth: number, startHeight: number, 
    keepAspect?: boolean, 
    aspectRatio?: number,
    centerBased?: boolean,
    targetIds?: string[],
    initialStates?: Map<string, { x: number; y: number; width: number; height: number }>
  ) => {
    // 다중 선택 모드 처리
    if (targetIds && targetIds.length > 1 && initialStates) {
      // 중앙 기준 리사이징 (Alt 키): 시작 핸들 위치 기준으로 마우스 이동 거리를 2배로 적용
      if (centerBased) {
        // 시작 시점의 핸들 위치 계산
        let startHandleX = startX;
        let startHandleY = startY;
        switch (handle) {
          case 'e':
          case 'ne':
          case 'se':
            startHandleX = startX + startWidth;
            break;
          case 'w':
          case 'nw':
          case 'sw':
            startHandleX = startX;
            break;
          case 'n':
          case 's':
            startHandleX = startX + startWidth / 2;
            break;
        }
        switch (handle) {
          case 's':
          case 'se':
          case 'sw':
            startHandleY = startY + startHeight;
            break;
          case 'n':
          case 'nw':
          case 'ne':
            startHandleY = startY;
            break;
          case 'e':
          case 'w':
            startHandleY = startY + startHeight / 2;
            break;
        }
        
        // 마우스 이동 거리 계산 (시작 핸들 위치 기준)
        const moveX = (newX - startHandleX) * 2; // 양쪽으로 확장되므로 2배
        const moveY = (newY - startHandleY) * 2; // 양쪽으로 확장되므로 2배
        
        let nw = startWidth;
        let nh = startHeight;
        
        // 핸들에 따라 크기 계산
        switch (handle) {
          case 'e':
            nw = Math.max(1, Math.round(startWidth + moveX));
            break;
          case 'w':
            nw = Math.max(1, Math.round(startWidth - moveX));
            break;
          case 's':
            nh = Math.max(1, Math.round(startHeight + moveY));
            break;
          case 'n':
            nh = Math.max(1, Math.round(startHeight - moveY));
            break;
          case 'se':
            nw = Math.max(1, Math.round(startWidth + moveX));
            nh = Math.max(1, Math.round(startHeight + moveY));
            break;
          case 'sw':
            nw = Math.max(1, Math.round(startWidth - moveX));
            nh = Math.max(1, Math.round(startHeight + moveY));
            break;
          case 'ne':
            nw = Math.max(1, Math.round(startWidth + moveX));
            nh = Math.max(1, Math.round(startHeight - moveY));
            break;
          case 'nw':
            nw = Math.max(1, Math.round(startWidth - moveX));
            nh = Math.max(1, Math.round(startHeight - moveY));
            break;
        }
        
        // 비율 고정 처리
        if (keepAspect && aspectRatio && nw > 0 && nh > 0) {
          const ratio = aspectRatio;
          // 중심점 기준으로 비율 계산
          if (Math.abs(moveX) > Math.abs(moveY)) {
            // 가로 기준
            nh = Math.max(1, Math.round(nw / ratio));
          } else {
            // 세로 기준
            nw = Math.max(1, Math.round(nh * ratio));
          }
        }
        
        // 중심을 고정하고 크기만 변경
        const centerX = startX + startWidth / 2;
        const centerY = startY + startHeight / 2;
        const nx = Math.round(centerX - nw / 2);
        const ny = Math.round(centerY - nh / 2);
        
        // 초기 AABB와 새 AABB의 중심 및 스케일 계산
        const initialAABBCenterX = startX + startWidth / 2;
        const initialAABBCenterY = startY + startHeight / 2;
        const newAABBCenterX = centerX; // 중심은 고정되므로 동일
        const newAABBCenterY = centerY; // 중심은 고정되므로 동일
        const scaleX = startWidth > 0 ? nw / startWidth : 1;
        const scaleY = startHeight > 0 ? nh / startHeight : 1;
        
        // 모든 객체를 비례적으로 스케일링
        targetIds.forEach(id => {
          const initialState = initialStates.get(id);
          if (!initialState) return;
          
          // 초기 AABB 중심 기준 상대 위치
          const initialObjCenterX = initialState.x + initialState.width / 2;
          const initialObjCenterY = initialState.y + initialState.height / 2;
          const relX = initialObjCenterX - initialAABBCenterX;
          const relY = initialObjCenterY - initialAABBCenterY;
          
          // 스케일 적용
          const newRelX = relX * scaleX;
          const newRelY = relY * scaleY;
          
          // 새 객체 중심 위치
          const newObjCenterX = newAABBCenterX + newRelX;
          const newObjCenterY = newAABBCenterY + newRelY;
          
          // 새 객체 크기
          const newObjWidth = Math.max(1, Math.round(initialState.width * scaleX));
          const newObjHeight = Math.max(1, Math.round(initialState.height * scaleY));
          
          // 새 객체 위치
          const newObjX = Math.round(newObjCenterX - newObjWidth / 2);
          const newObjY = Math.round(newObjCenterY - newObjHeight / 2);
          
          updateObject(id, (obj) => ({
            ...obj,
            x: newObjX,
            y: newObjY,
            width: newObjWidth,
            height: newObjHeight
          }));
        });
        
        return;
      }
      
      // 일반 리사이징 (기존 로직)
      // 새로운 AABB 계산
      let nx = startX, ny = startY, nw = startWidth, nh = startHeight;
      
      // 핸들에 따라 새로운 AABB 크기 계산
      switch (handle) {
        case 'nw': {
          if (newX <= startX + startWidth) {
            nx = Math.round(newX);
            nw = Math.round(startX + startWidth - newX);
          } else {
            nx = Math.round(startX + startWidth);
            nw = Math.round(newX - (startX + startWidth));
          }
          if (newY <= startY + startHeight) {
            ny = Math.round(newY);
            nh = Math.round(startY + startHeight - newY);
          } else {
            ny = Math.round(startY + startHeight);
            nh = Math.round(newY - (startY + startHeight));
          }
          break;
        }
        case 'n': {
          if (newY <= startY + startHeight) {
            ny = Math.round(newY);
            nh = Math.round(startY + startHeight - newY);
          } else {
            ny = Math.round(startY + startHeight);
            nh = Math.round(newY - (startY + startHeight));
          }
          break;
        }
        case 'ne': {
          if (newX >= startX) {
            nw = Math.round(newX - startX);
          } else {
            nx = Math.round(newX);
            nw = Math.round(startX - newX);
          }
          if (newY <= startY + startHeight) {
            ny = Math.round(newY);
            nh = Math.round(startY + startHeight - newY);
          } else {
            ny = Math.round(startY + startHeight);
            nh = Math.round(newY - (startY + startHeight));
          }
          break;
        }
        case 'w': {
          if (newX <= startX + startWidth) {
            nx = Math.round(newX);
            nw = Math.round(startX + startWidth - newX);
          } else {
            nx = Math.round(startX + startWidth);
            nw = Math.round(newX - (startX + startWidth));
          }
          break;
        }
        case 'e': {
          if (newX >= startX) {
            nw = Math.round(newX - startX);
          } else {
            nx = Math.round(newX);
            nw = Math.round(startX - newX);
          }
          break;
        }
        case 'sw': {
          if (newX <= startX + startWidth) {
            nx = Math.round(newX);
            nw = Math.round(startX + startWidth - newX);
          } else {
            nx = Math.round(startX + startWidth);
            nw = Math.round(newX - (startX + startWidth));
          }
          if (newY >= startY) {
            nh = Math.round(newY - startY);
          } else {
            ny = Math.round(newY);
            nh = Math.round(startY - newY);
          }
          break;
        }
        case 's': {
          if (newY >= startY) {
            nh = Math.round(newY - startY);
          } else {
            ny = Math.round(newY);
            nh = Math.round(startY - newY);
          }
          break;
        }
        case 'se': {
          if (newX >= startX) {
            nw = Math.round(newX - startX);
          } else {
            nx = Math.round(newX);
            nw = Math.round(startX - newX);
          }
          if (newY >= startY) {
            nh = Math.round(newY - startY);
          } else {
            ny = Math.round(newY);
            nh = Math.round(startY - newY);
          }
          break;
        }
      }
      
      // 비율 고정 처리
      if (keepAspect && aspectRatio && nw > 0 && nh > 0) {
        const ratio = aspectRatio;
        let anchorX = startX;
        let anchorY = startY;
        if (handle === 'se') { anchorX = startX; anchorY = startY; }
        else if (handle === 'sw') { anchorX = startX + startWidth; anchorY = startY; }
        else if (handle === 'ne') { anchorX = startX; anchorY = startY + startHeight; }
        else if (handle === 'nw') { anchorX = startX + startWidth; anchorY = startY + startHeight; }
        else if (handle === 'e') { anchorX = startX; anchorY = ny; }
        else if (handle === 'w') { anchorX = startX + startWidth; anchorY = ny; }
        else if (handle === 'n') { anchorX = nx; anchorY = startY + startHeight; }
        else if (handle === 's') { anchorX = nx; anchorY = startY; }
        
        let dragX = (handle === 'e' || handle === 'se' || handle === 'ne') ? nx + nw : nx;
        let dragY = (handle === 's' || handle === 'se' || handle === 'sw') ? ny + nh : ny;
        let dx = dragX - anchorX;
        let dy = dragY - anchorY;
        const sdx = Math.sign(dx) || 1;
        const sdy = Math.sign(dy) || 1;
        const adx = Math.abs(dx);
        const ady = Math.abs(dy);
        if (adx / (ady || 1) > ratio) {
          const newAdy = adx / ratio;
          dy = newAdy * sdy;
        } else {
          const newAdx = ady * ratio;
          dx = newAdx * sdx;
        }
        dragX = Math.round(anchorX + dx);
        dragY = Math.round(anchorY + dy);
        nx = Math.round(Math.min(anchorX, dragX));
        ny = Math.round(Math.min(anchorY, dragY));
        nw = Math.max(1, Math.round(Math.abs(dragX - anchorX)));
        nh = Math.max(1, Math.round(Math.abs(dragY - anchorY)));
      }
      
      if (nw < 1) nw = 1;
      if (nh < 1) nh = 1;
      
      // 초기 AABB와 새 AABB의 중심 및 스케일 계산
      const initialAABBCenterX = startX + startWidth / 2;
      const initialAABBCenterY = startY + startHeight / 2;
      const newAABBCenterX = nx + nw / 2;
      const newAABBCenterY = ny + nh / 2;
      const scaleX = startWidth > 0 ? nw / startWidth : 1;
      const scaleY = startHeight > 0 ? nh / startHeight : 1;
      
      // 모든 객체를 비례적으로 스케일링
      targetIds.forEach(id => {
        const initialState = initialStates.get(id);
        if (!initialState) return;
        
        // 초기 AABB 중심 기준 상대 위치
        const initialObjCenterX = initialState.x + initialState.width / 2;
        const initialObjCenterY = initialState.y + initialState.height / 2;
        const relX = initialObjCenterX - initialAABBCenterX;
        const relY = initialObjCenterY - initialAABBCenterY;
        
        // 스케일 적용
        const newRelX = relX * scaleX;
        const newRelY = relY * scaleY;
        
        // 새 객체 중심 위치
        const newObjCenterX = newAABBCenterX + newRelX;
        const newObjCenterY = newAABBCenterY + newRelY;
        
        // 새 객체 크기
        const newObjWidth = Math.max(1, Math.round(initialState.width * scaleX));
        const newObjHeight = Math.max(1, Math.round(initialState.height * scaleY));
        
        // 새 객체 위치
        const newObjX = Math.round(newObjCenterX - newObjWidth / 2);
        const newObjY = Math.round(newObjCenterY - newObjHeight / 2);
        
        updateObject(id, (obj) => ({
          ...obj,
          x: newObjX,
          y: newObjY,
          width: newObjWidth,
          height: newObjHeight
        }));
      });
      
      return;
    }
    
    // 단일 선택 모드 (기존 로직)
    if (!selectedId) return;
    
    // 중앙 기준 리사이징 (Alt 키): 시작 핸들 위치 기준으로 마우스 이동 거리를 2배로 적용
    if (centerBased) {
      // 시작 시점의 핸들 위치 계산
      let startHandleX = startX;
      let startHandleY = startY;
      switch (handle) {
        case 'e':
        case 'ne':
        case 'se':
          startHandleX = startX + startWidth;
          break;
        case 'w':
        case 'nw':
        case 'sw':
          startHandleX = startX;
          break;
        case 'n':
        case 's':
          startHandleX = startX + startWidth / 2;
          break;
      }
      switch (handle) {
        case 's':
        case 'se':
        case 'sw':
          startHandleY = startY + startHeight;
          break;
        case 'n':
        case 'nw':
        case 'ne':
          startHandleY = startY;
          break;
        case 'e':
        case 'w':
          startHandleY = startY + startHeight / 2;
          break;
      }
      
      // 마우스 이동 거리 계산 (시작 핸들 위치 기준)
      const moveX = (newX - startHandleX) * 2; // 양쪽으로 확장되므로 2배
      const moveY = (newY - startHandleY) * 2; // 양쪽으로 확장되므로 2배
      
      let nw = startWidth;
      let nh = startHeight;
      let nx = startX;
      let ny = startY;
      
      // 핸들에 따라 크기 계산
      switch (handle) {
        case 'e':
          nw = Math.max(1, Math.round(startWidth + moveX));
          break;
        case 'w':
          nw = Math.max(1, Math.round(startWidth - moveX));
          break;
        case 's':
          nh = Math.max(1, Math.round(startHeight + moveY));
          break;
        case 'n':
          nh = Math.max(1, Math.round(startHeight - moveY));
          break;
        case 'se':
          nw = Math.max(1, Math.round(startWidth + moveX));
          nh = Math.max(1, Math.round(startHeight + moveY));
          break;
        case 'sw':
          nw = Math.max(1, Math.round(startWidth - moveX));
          nh = Math.max(1, Math.round(startHeight + moveY));
          break;
        case 'ne':
          nw = Math.max(1, Math.round(startWidth + moveX));
          nh = Math.max(1, Math.round(startHeight - moveY));
          break;
        case 'nw':
          nw = Math.max(1, Math.round(startWidth - moveX));
          nh = Math.max(1, Math.round(startHeight - moveY));
          break;
      }
      
      // 중심을 고정하고 크기만 변경
      const centerX = startX + startWidth / 2;
      const centerY = startY + startHeight / 2;
      nx = Math.round(centerX - nw / 2);
      ny = Math.round(centerY - nh / 2);
      
      // 비율 고정(Shift) 처리
      if (keepAspect && aspectRatio && nw > 0 && nh > 0) {
        const ratio = aspectRatio;
        // 중심점 기준으로 비율 계산
        if (Math.abs(moveX) > Math.abs(moveY)) {
          // 가로 기준
          nh = Math.max(1, Math.round(nw / ratio));
        } else {
          // 세로 기준
          nw = Math.max(1, Math.round(nh * ratio));
        }
        nx = Math.round(centerX - nw / 2);
        ny = Math.round(centerY - nh / 2);
      }
      
      updateObject(selectedId, (obj) => ({
        ...obj,
        x: nx,
        y: ny,
        width: nw,
        height: nh
      }));
      return;
    }
    
    // 일반 리사이징 (기존 로직)
    let nx = startX, ny = startY, nw = startWidth, nh = startHeight;
    switch (handle) {
      case 'nw': {
        if (newX <= startX + startWidth) {
          nx = Math.round(newX);
          nw = Math.round(startX + startWidth - newX);
        } else {
          nx = Math.round(startX + startWidth);
          nw = Math.round(newX - (startX + startWidth));
        }
        if (newY <= startY + startHeight) {
          ny = Math.round(newY);
          nh = Math.round(startY + startHeight - newY);
        } else {
          ny = Math.round(startY + startHeight);
          nh = Math.round(newY - (startY + startHeight));
        }
        break;
      }
      case 'n': {
        if (newY <= startY + startHeight) {
          ny = Math.round(newY);
          nh = Math.round(startY + startHeight - newY);
        } else {
          ny = Math.round(startY + startHeight);
          nh = Math.round(newY - (startY + startHeight));
        }
        break;
      }
      case 'ne': {
        if (newX >= startX) {
          nw = Math.round(newX - startX);
        } else {
          nx = Math.round(newX);
          nw = Math.round(startX - newX);
        }
        if (newY <= startY + startHeight) {
          ny = Math.round(newY);
          nh = Math.round(startY + startHeight - newY);
        } else {
          ny = Math.round(startY + startHeight);
          nh = Math.round(newY - (startY + startHeight));
        }
        break;
      }
      case 'w': {
        if (newX <= startX + startWidth) {
          nx = Math.round(newX);
          nw = Math.round(startX + startWidth - newX);
        } else {
          nx = Math.round(startX + startWidth);
          nw = Math.round(newX - (startX + startWidth));
        }
        break;
      }
      case 'e': {
        if (newX >= startX) {
          nw = Math.round(newX - startX);
        } else {
          nx = Math.round(newX);
          nw = Math.round(startX - newX);
        }
        break;
      }
      case 'sw': {
        if (newX <= startX + startWidth) {
          nx = Math.round(newX);
          nw = Math.round(startX + startWidth - newX);
        } else {
          nx = Math.round(startX + startWidth);
          nw = Math.round(newX - (startX + startWidth));
        }
        if (newY >= startY) {
          nh = Math.round(newY - startY);
        } else {
          ny = Math.round(newY);
          nh = Math.round(startY - newY);
        }
        break;
      }
      case 's': {
        if (newY >= startY) {
          nh = Math.round(newY - startY);
        } else {
          ny = Math.round(newY);
          nh = Math.round(startY - newY);
        }
        break;
      }
      case 'se': {
        if (newX >= startX) {
          nw = Math.round(newX - startX);
        } else {
          nx = Math.round(newX);
          nw = Math.round(startX - newX);
        }
        if (newY >= startY) {
          nh = Math.round(newY - startY);
        } else {
          ny = Math.round(newY);
          nh = Math.round(startY - newY);
        }
        break;
      }
    }

    // 비율 고정(Shift) 처리
    if (keepAspect && aspectRatio && nw > 0 && nh > 0) {
      const ratio = aspectRatio;
      // 선택된 핸들에 따라 기준 코너(Anchor) 결정
      let anchorX = startX;
      let anchorY = startY;
      if (handle === 'se') { anchorX = startX; anchorY = startY; }
      else if (handle === 'sw') { anchorX = startX + startWidth; anchorY = startY; }
      else if (handle === 'ne') { anchorX = startX; anchorY = startY + startHeight; }
      else if (handle === 'nw') { anchorX = startX + startWidth; anchorY = startY + startHeight; }
      else if (handle === 'e') { anchorX = startX; anchorY = ny; }
      else if (handle === 'w') { anchorX = startX + startWidth; anchorY = ny; }
      else if (handle === 'n') { anchorX = nx; anchorY = startY + startHeight; }
      else if (handle === 's') { anchorX = nx; anchorY = startY; }

      // 현재 박스의 대각선 방향 길이 재계산
      let dx = (nx + (nw * (handle === 'e' || handle === 'se' || handle === 'ne' ? 1 : 0 ? 1 : 0))) - anchorX;
      let dy = (ny + (nh * (handle === 's' || handle === 'se' || handle === 'sw' ? 1 : 0 ? 1 : 0))) - anchorY;
      // 위 계산은 복잡하므로 대신 드래그 지점 추정: 박스의 반대 코너로부터의 벡터를 사용
      // 드래그 방향 보정을 위해, 드래그 결과(nx,ny,nw,nh)로부터 드래그 코너 좌표를 복원
      let dragX = (handle === 'e' || handle === 'se' || handle === 'ne') ? nx + nw : nx;
      let dragY = (handle === 's' || handle === 'se' || handle === 'sw') ? ny + nh : ny;
      dx = dragX - anchorX;
      dy = dragY - anchorY;
      const sdx = Math.sign(dx) || 1;
      const sdy = Math.sign(dy) || 1;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      if (adx / (ady || 1) > ratio) {
        // 가로가 더 큼 -> 세로를 맞춤
        const newAdy = adx / ratio;
        dy = newAdy * sdy;
      } else {
        // 세로가 더 큼 -> 가로를 맞춤
        const newAdx = ady * ratio;
        dx = newAdx * sdx;
      }
      // 새 드래그 코너 좌표
      dragX = Math.round(anchorX + dx);
      dragY = Math.round(anchorY + dy);
      // 새 박스 복원
      nx = Math.round(Math.min(anchorX, dragX));
      ny = Math.round(Math.min(anchorY, dragY));
      nw = Math.max(1, Math.round(Math.abs(dragX - anchorX)));
      nh = Math.max(1, Math.round(Math.abs(dragY - anchorY)));
    }

    if (nw < 1) nw = 1;
    if (nh < 1) nh = 1;
    
    updateObject(selectedId, (obj) => ({
      ...obj,
      x: nx,
      y: ny,
      width: nw,
      height: nh
    }));
  }, [selectedId, updateObject]);

  // 포인트 히트테스트 (zOrder 상단 우선) - 회전 고려, 클리핑 마스크 고려
  const hitTestPoint = useCallback((px: number, py: number): string | null => {
    for (let i = zOrder.length - 1; i >= 0; i--) {
      const id = zOrder[i];
      const obj = objects.get(id);
      if (!obj) continue;
      
      // 회전 고려 히트테스트
      const angle = obj.rotation || 0;
      let tx = px;
      let ty = py;
      if (Math.abs(angle) > 1e-6) {
        const cx = obj.x + obj.width / 2;
        const cy = obj.y + obj.height / 2;
        const dx = px - cx;
        const dy = py - cy;
        const cos = Math.cos(-angle);
        const sin = Math.sin(-angle);
        tx = cx + dx * cos - dy * sin;
        ty = cy + dx * sin + dy * cos;
      }
      
      // 객체 모양 체크
      let isInside = false;
      if (obj.type === 'rectangle') {
        isInside = tx >= obj.x && tx <= obj.x + obj.width && ty >= obj.y && ty <= obj.y + obj.height;
      } else if (obj.type === 'circle') {
        const cx = obj.x + obj.width / 2;
        const cy = obj.y + obj.height / 2;
        const rx = obj.width / 2;
        const ry = obj.height / 2;
        if (rx > 0 && ry > 0) {
          const nx = (tx - cx) / rx;
          const ny = (ty - cy) / ry;
          isInside = nx * nx + ny * ny <= 1;
        }
      } else if (obj.type === 'image') {
        isInside = tx >= obj.x && tx <= obj.x + obj.width && ty >= obj.y && ty <= obj.y + obj.height;
      } else if (obj.type === 'text') {
        isInside = tx >= obj.x && tx <= obj.x + obj.width && ty >= obj.y && ty <= obj.y + obj.height;
      }
      
      if (!isInside) continue;
      
      // 클리핑 마스크 체크: 클리핑된 객체는 마스크 영역 내에서만 선택 가능
      if (obj.isClipped && obj.clipMaskId) {
        const maskObj = objects.get(obj.clipMaskId);
        if (maskObj) {
          // 마스크 객체의 회전 고려
          const maskAngle = maskObj.rotation || 0;
          let maskTx = px;
          let maskTy = py;
          if (Math.abs(maskAngle) > 1e-6) {
            const mcx = maskObj.x + maskObj.width / 2;
            const mcy = maskObj.y + maskObj.height / 2;
            const mdx = px - mcx;
            const mdy = py - mcy;
            const mcos = Math.cos(-maskAngle);
            const msin = Math.sin(-maskAngle);
            maskTx = mcx + mdx * mcos - mdy * msin;
            maskTy = mcy + mdx * msin + mdy * mcos;
          }
          
          // 마스크 영역 체크
          let isInMask = false;
          if (maskObj.type === 'rectangle') {
            isInMask = maskTx >= maskObj.x && maskTx <= maskObj.x + maskObj.width && 
                      maskTy >= maskObj.y && maskTy <= maskObj.y + maskObj.height;
          } else if (maskObj.type === 'circle') {
            const mcx = maskObj.x + maskObj.width / 2;
            const mcy = maskObj.y + maskObj.height / 2;
            const mrx = maskObj.width / 2;
            const mry = maskObj.height / 2;
            if (mrx > 0 && mry > 0) {
              const mnx = (maskTx - mcx) / mrx;
              const mny = (maskTy - mcy) / mry;
              isInMask = mnx * mnx + mny * mny <= 1;
            }
          } else {
            // image, text 등은 직사각형
            isInMask = maskTx >= maskObj.x && maskTx <= maskObj.x + maskObj.width && 
                      maskTy >= maskObj.y && maskTy <= maskObj.y + maskObj.height;
          }
          
          if (!isInMask) continue;
        }
      }
      
      return id;
    }
    return null;
  }, [objects, zOrder]);

  // 객체 추가 (이미지 등)
  const addObject = useCallback((obj: CanvasObject) => {
    setObjects(prev => {
      const next = new Map(prev);
      next.set(obj.id, obj);
      return next;
    });
    setZOrder(prev => [...prev, obj.id]);
  }, []);

  // 객체 삭제
  const deleteObject = useCallback((id: string) => {
    setObjects(prev => {
      const next = new Map(prev);
      
      // 삭제되는 객체가 마스크 객체인지 확인하고, 클리핑된 객체들의 클리핑 해제
      const clippedObjects: string[] = [];
      prev.forEach((obj, objId) => {
        if (obj.clipMaskId === id && obj.isClipped) {
          clippedObjects.push(objId);
        }
      });
      
      // 클리핑된 객체들의 클리핑 해제
      clippedObjects.forEach(clippedId => {
        const clippedObj = next.get(clippedId);
        if (clippedObj) {
          next.set(clippedId, {
            ...clippedObj,
            clipMaskId: undefined,
            isClipped: false
          });
        }
      });
      
      // 마스크 객체 삭제
      next.delete(id);
      return next;
    });
    setZOrder(prev => prev.filter(oid => oid !== id));
    // 선택된 객체가 삭제되면 선택 해제
    if (selectedId === id) {
      setSelectedId(null);
    }
    // 다중 선택에서도 제거
    if (selectedIds.has(id)) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [selectedId, selectedIds]);

  // zOrder에서 객체들 이동 (레이어 드래그용)
  const moveObjectsInZOrder = useCallback((
    draggedIds: string[],
    targetId: string,
    insertAbove: boolean
  ) => {
    if (draggedIds.length === 0 || !targetId) return;
    
    setZOrder(prev => {
      const newZOrder = [...prev];
      
      // 드래그된 ID들이 모두 zOrder에 있는지 확인
      const allExist = draggedIds.every(id => newZOrder.includes(id));
      if (!allExist) return prev;
      
      // 타겟 ID가 zOrder에 있는지 확인
      const targetIndex = newZOrder.indexOf(targetId);
      if (targetIndex === -1) return prev;
      
      // 드래그된 객체들을 원본 zOrder의 순서대로 정렬
      const sortedDraggedIds = draggedIds
        .map(id => ({ id, index: newZOrder.indexOf(id) }))
        .filter(item => item.index !== -1)
        .sort((a, b) => a.index - b.index)
        .map(item => item.id);
      
      // 정렬된 순서로 draggedObjects 생성 (원본 zOrder의 상대적 순서 유지)
      const draggedObjects = sortedDraggedIds
        .map(id => newZOrder[newZOrder.indexOf(id)])
        .filter(obj => obj !== undefined);
      
      // 드래그된 객체들을 제거 (역순으로 제거하여 인덱스 변경 방지)
      const indicesToRemove = sortedDraggedIds
        .map(id => newZOrder.indexOf(id))
        .filter(idx => idx !== -1)
        .sort((a, b) => b - a);
      
      indicesToRemove.forEach(idx => {
        newZOrder.splice(idx, 1);
      });
      
      // 타겟 인덱스 재계산 (제거된 항목들 때문에 인덱스가 변경됨)
      let targetIndexAfterRemoval = targetIndex;
      const removedBeforeTarget = indicesToRemove.filter(idx => idx < targetIndex).length;
      targetIndexAfterRemoval = targetIndex - removedBeforeTarget;
      
      // 삽입 위치 계산
      // 레이어 패널이 reverse로 표시되기 때문에, insertAbove의 의미를 반대로 적용
      // insertAbove = true (마우스가 중심보다 위) → 타겟 뒤에 삽입 (높은 인덱스) → 화면에서는 타겟 위로 이동
      // insertAbove = false (마우스가 중심보다 아래) → 타겟 앞에 삽입 (낮은 인덱스) → 화면에서는 타겟 아래로 이동
      let insertIndex = targetIndexAfterRemoval;
      if (insertAbove) {
        insertIndex = targetIndexAfterRemoval + 1;  // 타겟 뒤에 삽입 (높은 인덱스)
      } else {
        insertIndex = targetIndexAfterRemoval;  // 타겟 앞에 삽입 (낮은 인덱스)
      }
      
      // 드래그된 객체들 삽입
      newZOrder.splice(insertIndex, 0, ...draggedObjects);
      
      return newZOrder;
    });
  }, []);

  // 단축키용 zOrder 이동 (클리핑 세트 보존 + 다중 선택 압축 이동)
  const moveSelectedInZOrderByShortcut = useCallback((
    direction: 'up' | 'down',
    moveType: 'step' | 'extreme' = 'step'
  ) => {
    const baseSelectedIds = selectedIds.size > 0
      ? Array.from(selectedIds)
      : (selectedId ? [selectedId] : []);
    if (baseSelectedIds.length === 0) return false;

    let moved = false;
    setZOrder(prev => {
      if (prev.length <= 1) return prev;

      const indexMap = new Map<string, number>();
      prev.forEach((id, idx) => indexMap.set(id, idx));

      // 마스크 ID -> 해당 마스크 세트(마스크 + 클립된 레이어) ID 목록
      const clipSetByMaskId = new Map<string, string[]>();
      const maskOfId = new Map<string, string>();

      objects.forEach((obj) => {
        if (obj.isClipped && obj.clipMaskId) {
          const maskId = obj.clipMaskId;
          if (!clipSetByMaskId.has(maskId)) {
            clipSetByMaskId.set(maskId, [maskId]);
          }
          const set = clipSetByMaskId.get(maskId)!;
          if (!set.includes(obj.id)) {
            set.push(obj.id);
          }
        }
      });

      clipSetByMaskId.forEach((ids, maskId) => {
        const sorted = ids
          .filter(id => indexMap.has(id))
          .sort((a, b) => (indexMap.get(a)! - indexMap.get(b)!));
        clipSetByMaskId.set(maskId, sorted);
        sorted.forEach(id => maskOfId.set(id, maskId));
      });

      // 클리핑 세트 일부 선택 시 세트 전체를 선택으로 승격
      const expandedSelected = new Set<string>(baseSelectedIds);
      baseSelectedIds.forEach((id) => {
        const obj = objects.get(id);
        let maskId: string | undefined;
        if (obj?.isClipped && obj.clipMaskId) {
          maskId = obj.clipMaskId;
        } else if (clipSetByMaskId.has(id)) {
          maskId = id;
        }
        if (maskId) {
          (clipSetByMaskId.get(maskId) || []).forEach(setId => expandedSelected.add(setId));
        }
      });

      type Unit = { ids: string[]; isSelected: boolean };
      const units: Unit[] = [];
      const consumed = new Set<string>();

      for (const id of prev) {
        if (consumed.has(id)) continue;
        const maskId = maskOfId.get(id);
        if (maskId) {
          const setIds = clipSetByMaskId.get(maskId) || [id];
          setIds.forEach(setId => consumed.add(setId));
          units.push({
            ids: setIds,
            isSelected: setIds.some(setId => expandedSelected.has(setId))
          });
        } else {
          consumed.add(id);
          units.push({
            ids: [id],
            isSelected: expandedSelected.has(id)
          });
        }
      }

      const selectedUnits = units.filter(u => u.isSelected);
      if (selectedUnits.length === 0) return prev;
      const unselectedUnits = units.filter(u => !u.isSelected);

      let newUnits: Unit[] = units;
      const singleSelectionMode = baseSelectedIds.length === 1;

      if (singleSelectionMode) {
        const selectedIdx = units.findIndex(u => u.isSelected);
        if (selectedIdx === -1) return prev;
        if (moveType === 'extreme') {
          if (direction === 'up') {
            if (selectedIdx === units.length - 1) return prev;
            newUnits = [
              ...units.slice(0, selectedIdx),
              ...units.slice(selectedIdx + 1),
              units[selectedIdx]
            ];
          } else {
            if (selectedIdx === 0) return prev;
            newUnits = [
              units[selectedIdx],
              ...units.slice(0, selectedIdx),
              ...units.slice(selectedIdx + 1)
            ];
          }
        } else {
          const targetIdx = direction === 'up' ? selectedIdx + 1 : selectedIdx - 1;
          if (targetIdx < 0 || targetIdx >= units.length) return prev;

          newUnits = [...units];
          const temp = newUnits[selectedIdx];
          newUnits[selectedIdx] = newUnits[targetIdx];
          newUnits[targetIdx] = temp;
        }
      } else {
        if (unselectedUnits.length === 0) return prev;
        if (moveType === 'extreme') {
          if (direction === 'up') {
            newUnits = [...unselectedUnits, ...selectedUnits];
          } else {
            newUnits = [...selectedUnits, ...unselectedUnits];
          }
        } else {
          // 다중 선택: 비연속 선택을 압축 블록으로 보고 1스텝씩 이동
          const selectedIndices = units
            .map((u, idx) => (u.isSelected ? idx : -1))
            .filter(idx => idx !== -1);
          if (selectedIndices.length === 0) return prev;

          const bottomSelectedIdx = Math.min(...selectedIndices);
          const currentSlot = units
            .slice(0, bottomSelectedIdx)
            .filter(u => !u.isSelected).length;

          const nextSlot = direction === 'up' ? currentSlot + 1 : currentSlot - 1;
          if (nextSlot < 0 || nextSlot > unselectedUnits.length) return prev;

          newUnits = [
            ...unselectedUnits.slice(0, nextSlot),
            ...selectedUnits,
            ...unselectedUnits.slice(nextSlot)
          ];
        }
      }

      const flattened = newUnits.flatMap(unit => unit.ids);
      const changed =
        flattened.length === prev.length &&
        flattened.some((id, idx) => id !== prev[idx]);
      if (!changed) return prev;

      moved = true;
      return flattened;
    });

    return moved;
  }, [objects, selectedId, selectedIds]);

  // 히스토리 복원용 상태 설정
  const restoreState = useCallback((state: { objects: Map<string, CanvasObject>; zOrder: string[]; selectedId: string | null }) => {
    setObjects(new Map(state.objects));
    setZOrder([...state.zOrder]);
    setSelectedId(state.selectedId);
  }, []);

  return {
    objects,
    zOrder,
    setZOrder,
    setObjects,
    getObjectsArray,
    getObjectById,
    tempRectRef,
    beginRect,
    beginCircle,
    updateRect,
    updateCircle,
    commitRect,
    commitCircle,
    clearTemp,
    selectedId,
    setSelectedId,
    selectedIds,
    setSelectedIds,
    addToSelection,
    removeFromSelection,
    setSelection,
    clearSelection,
    getSelectedObjects,
    hitTestPoint,
    updateObject,
    moveSelectedBy,
    resizeSelected,
    rotateSelectedBy,
    addObject,
    deleteObject,
    moveObjectsInZOrder,
    moveSelectedInZOrderByShortcut,
    restoreState
  };
}


